import { Center, Link, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { getTenantUrl } from '@duke-hq/lib';
import { useNavigate } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useCallback } from 'react';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import type { SortFilters } from './ProvidersPage/index';
import { dayjsFromNow } from '@duke-hq/djs';
import { ExternalLink, Receipt } from 'lucide-react';

function EmptyState({ isLoading }: { isLoading: boolean }) {
  return (
    <Tr>
      <Td colSpan={7}>
        <Center>
          {isLoading ? <Spinner size="xl" /> : <Text textAlign="center">No Providers</Text>}
        </Center>
      </Td>
    </Tr>
  );
}

function Row({ provider }: { provider: RouterOutputs['providers']['all'][0] }) {
  const navigate = useNavigate();
  const domainUrl = getTenantUrl(provider.subdomain);

  const handleClick = useCallback(() => {
    navigate(`${provider.id}/settings`);
  }, [navigate, provider.id]);

  return (
    <Tr
      key={provider.id}
      cursor="pointer"
      _hover={{ bg: 'bg.subtle' }}
      onClick={handleClick}
      color="fg.muted"
      fontWeight="semimedium"
    >
      <Td>{provider.name}</Td>
      <Td>{provider.subdomain}</Td>
      <Td>
        <Link
          href={domainUrl}
          color="fg.muted"
          _hover={{}} // Disable hover effect
          isExternal
          onClick={(event) => {
            event.stopPropagation();
          }}
          display="inline-flex"
        >
          {domainUrl}&nbsp;
          <ExternalLink size={16} strokeWidth={2} />
        </Link>
      </Td>
      <Td>{provider.defaultEmailDomain ? `@${provider.defaultEmailDomain}` : 'Not provided'}</Td>
      <Td>{provider.is_billable ? <Receipt strokeWidth={1} /> : null}</Td>
      <Td>{dayjsFromNow(provider.updated_at)}</Td>
      <Td>{dayjsFromNow(provider.created_at)}</Td>
    </Tr>
  );
}

export function ProvidersTable({
  providers,
  filters,
  onSort,
  isLoading,
}: {
  providers: RouterOutputs['providers']['all'];
  filters: SortFilters;
  onSort: (name: string) => void;
  isLoading: boolean;
}) {
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>
            <SortableHeader
              label={t`Name`}
              columnName="name"
              currentOrderBy={filters.orderBy}
              currentSortOrder={filters.sort}
              onSortChange={onSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Prefix`}
              columnName="schema"
              currentOrderBy={filters.orderBy}
              currentSortOrder={filters.sort}
              onSortChange={onSort}
            />
          </Th>
          <Th>
            <Trans>Domain</Trans>
          </Th>
          <Th>
            <Trans>Default email domain</Trans>
          </Th>
          <Th>
            <SortableHeader
              label={t`Billable`}
              columnName="isBillable"
              currentOrderBy={filters.orderBy}
              currentSortOrder={filters.sort}
              onSortChange={onSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Last updated`}
              columnName="updatedAt"
              currentOrderBy={filters.orderBy}
              currentSortOrder={filters.sort}
              onSortChange={onSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Created`}
              columnName="createdAt"
              currentOrderBy={filters.orderBy}
              currentSortOrder={filters.sort}
              onSortChange={onSort}
            />
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {Array.isArray(providers) && providers.length !== 0 ? (
          providers.map((provider) => <Row key={provider.id} provider={provider} />)
        ) : (
          <EmptyState isLoading={isLoading} />
        )}
      </Tbody>
    </Table>
  );
}
